import { z } from 'zod';

type RequestSourceOptions = {
  environment?: 'local' | 'test' | 'live' | (string & {});
  name: string;
  modifier?: string;
  action?: 'create' | 'edit';
};

const RequestSourceParser = z.object({
  environment: z.union([z.literal('local'), z.literal('test'), z.literal('live')]).default('live'),
  name: z.string(),
  modifier: z.string().default('default'),
  action: z.union([z.literal('create'), z.literal('edit')]).default('create')
});

type RequestSource = z.infer<typeof RequestSourceParser>;

export function stringifySource(requestSource: RequestSourceOptions) {
  const { environment, name, modifier, action } = RequestSourceParser.parse(requestSource);
  return `${environment}-${name}-${modifier}-${action}`;
}

export function parseSource(source: string): RequestSource {
  const [environment, name, modifier, action] = source.split('-');

  return RequestSourceParser.parse({
    environment,
    name,
    modifier,
    action
  });
}

export function getProductFromRequestSource(sourceName: string) {
  if (sourceName.includes('blueprintIndex')) {
    return 'blueprintIndex';
  }
  if (sourceName.includes('blueprintCollection')) {
    return 'blueprintCollection';
  }
  if (sourceName.includes('blueprintDestination')) {
    return 'blueprintDestination';
  }
  if (sourceName.includes('bps')) {
    return 'bps';
  }
  if (sourceName.includes('playground')) {
    return 'playground';
  }
  if (sourceName.includes('blueprint')) {
    return 'blueprint';
  }
  return 'unknownSource';
}

/** parseSource throws on legacy and SBS-shaped strings; callers reading stored sources want a miss, not a 500. */
export function safeParseSource(source: unknown): RequestSource | undefined {
  if (typeof source !== 'string' || !source) return undefined;
  try {
    return parseSource(source);
  } catch {
    return undefined;
  }
}

/** What a journey already carries: the serialized string from the engine, or the parsed object off `user`. */
type PreviousRequestSource = string | Pick<RequestSource, 'name' | 'modifier'>;

type BuildRequestSourceArgs = {
  /** `?source=` from the URL, when the current navigation carries one. */
  source?: string | null;
  /** `?lp=` from the URL — the landing page a trip was started from. */
  lp?: string | null;
  /** The journey's own requestSource, for calls continuing an existing trip. */
  previous?: PreviousRequestSource | null;
  action: 'create' | 'edit';
  /** Pass `process.env.NEXT_PUBLIC_SOURCE_ENV`. Required because this package has no node types. */
  environment: string | undefined;
};

/**
 * Builds a requestSource with one shared fallback chain: the URL, then the journey's existing value,
 * then a default. That middle step keeps landing-page attribution alive across an edit, which has no
 * `lp` in the URL. `modifier` must stay hyphen-free — stringifySource joins segments with '-'.
 */
export function buildRequestSource({ source, lp, previous, action, environment }: BuildRequestSourceArgs) {
  const previousSource = typeof previous === 'string' ? safeParseSource(previous) : (previous ?? undefined);

  // `||` not `??`: a bare `?lp=` or `?source=` arrives as an empty string and must not beat the stored value.
  return stringifySource({
    environment,
    name: source || previousSource?.name || 'blueprint',
    modifier: lp || previousSource?.modifier || undefined,
    action
  });
}
